/**
 * CSS health detection for iOS Safari / mobile browsers.
 *
 * After long background (often 3–4 hours), iOS can purge stylesheets from memory
 * while JavaScript and React state remain alive. The page renders but looks unstyled.
 *
 * Uses a bundled CSS custom property and a hidden probe element so we can detect
 * missing styles on visibility resume and trigger a full reload.
 */

import { CSS_HEALTH } from '../../constants/common';

const { PROBE_ELEMENT_ID, LOADED_VAR, LOADED_VAR_VALUE, RELOAD_ATTEMPT_KEY, MAX_RELOAD_ATTEMPTS } =
  CSS_HEALTH;

/**
 * Inserts a hidden DOM node probed by {@link isCssHealthy}. Safe to call multiple times.
 */
export function ensureCssHealthProbe() {
  if (typeof document === 'undefined') return;

  if (document.getElementById(PROBE_ELEMENT_ID)) return;

  const probe = document.createElement('div');
  probe.id = PROBE_ELEMENT_ID;
  probe.setAttribute('aria-hidden', 'true');
  probe.setAttribute('data-css-health-probe', 'true');

  const mountTarget = document.body || document.documentElement;
  mountTarget.appendChild(probe);
}

/**
 * Returns true when bundled app CSS is applied (custom property + probe layout).
 * @returns {boolean}
 */
export function isCssHealthy() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return true;

  const rootStyle = window.getComputedStyle(document.documentElement);
  const loadedFlag = rootStyle.getPropertyValue(LOADED_VAR).trim();
  if (loadedFlag !== LOADED_VAR_VALUE) {
    return false;
  }

  const probe = document.getElementById(PROBE_ELEMENT_ID);
  if (probe) {
    const probeStyle = window.getComputedStyle(probe);
    if (probeStyle.visibility !== 'hidden' || probeStyle.position !== 'absolute') {
      return false;
    }
  }

  return hasActiveStylesheetRules();
}

/**
 * True if at least one same-origin stylesheet still exposes rules.
 * Cross-origin sheets may throw; treat those as present.
 * @returns {boolean}
 */
function hasActiveStylesheetRules() {
  try {
    const sheets = document.styleSheets;
    if (!sheets || sheets.length === 0) return false;

    let sawSameOriginSheet = false;

    for (let i = 0; i < sheets.length; i += 1) {
      try {
        sawSameOriginSheet = true;
        const rules = sheets[i].cssRules;
        if (rules && rules.length > 0) return true;
      } catch {
        // Cross-origin stylesheets (fonts, CDN) — skip; app bundles are same-origin.
      }
    }

    // Same-origin sheets exist but all empty → likely purged. No same-origin sheets
    // at all is inconclusive (e.g. before first paint); rely on other probes.
    return !sawSameOriginSheet;
  } catch {
    return true;
  }
}

function clearCssReloadAttempts() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
  } catch {
    // Ignore private mode / storage errors
  }
}

/**
 * Full page reload when styles were purged. Bypasses payment/call guards because
 * the UI is unusable without CSS. Caps attempts per tab to avoid reload loops.
 * @returns {boolean} true if a reload was triggered
 */
export function reloadIfCssUnhealthy() {
  if (isCssHealthy()) {
    clearCssReloadAttempts();
    return false;
  }

  if (typeof window === 'undefined') return false;

  try {
    const raw = sessionStorage.getItem(RELOAD_ATTEMPT_KEY);
    const attempts = raw ? parseInt(raw, 10) : 0;
    if (Number.isFinite(attempts) && attempts >= MAX_RELOAD_ATTEMPTS) {
      return false;
    }
    sessionStorage.setItem(RELOAD_ATTEMPT_KEY, String((Number.isFinite(attempts) ? attempts : 0) + 1));
  } catch {
    // If sessionStorage fails, still attempt one reload
  }

  window.location.reload();
  return true;
}
